import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Trash2, Edit, Play, Download, Calendar } from 'lucide-react';
import { backendClient } from '@/lib/backendClient';
import { useToast } from '@/hooks/use-toast';

interface SampleSession {
  id: string;
  title: string;
  category_id: string;
  content: string;
  duration: number;
  tags: string[];
  audio_url: string | null;
  created_at: string;
  updated_at: string;
}

interface Category {
  id: string;
  name: string;
  color: string;
}

const ContentManagement: React.FC = () => {
  const [sessions, setSessions] = useState<SampleSession[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [sessionsResult, categoriesResult] = await Promise.all([
        backendClient.from('sample_sessions').select('*').order('created_at', { ascending: false }),
        backendClient.from('categories').select('id, name, color')
      ]);

      if (sessionsResult.error) throw sessionsResult.error;
      if (categoriesResult.error) throw categoriesResult.error;

      setSessions(sessionsResult.data || []);
      setCategories(categoriesResult.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load content data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteSession = async (sessionId: string) => {
    if (!confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await backendClient
        .from('sample_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) throw error;

      setSessions(sessions.filter(s => s.id !== sessionId));
      toast({
        title: "Session deleted",
        description: "The sample session has been removed successfully.",
      });
    } catch (error) {
      console.error('Error deleting session:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete the session.",
      });
    }
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  const getCategoryColor = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.color || '#gray';
  };

  const filteredSessions = selectedCategory === 'all' 
    ? sessions 
    : sessions.filter(s => s.category_id === selectedCategory);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Content Management
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Manage sample sessions and meditation content across all categories.
          </p>
        </CardHeader>
        <CardContent>
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-6">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory('all')}
            >
              All Categories ({sessions.length})
            </Button>
            {categories.map((category) => {
              const count = sessions.filter(s => s.category_id === category.id).length;
              return (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.name} ({count})
                </Button>
              );
            })}
          </div>

          {/* Sessions List */}
          <div className="space-y-4">
            {filteredSessions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No sessions found for the selected category.
              </div>
            ) : (
              filteredSessions.map((session) => (
                <Card key={session.id} className="border-l-4" style={{ borderLeftColor: getCategoryColor(session.category_id) }}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold truncate">{session.title}</h3>
                          <Badge variant="secondary" className="shrink-0">
                            {getCategoryName(session.category_id)}
                          </Badge>
                          {session.duration && (
                            <Badge variant="outline" className="shrink-0">
                              {Math.round(session.duration / 60)}min
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                          {session.content.substring(0, 200)}...
                        </p>
                        
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Created: {new Date(session.created_at).toLocaleDateString()}
                          </div>
                          {session.updated_at !== session.created_at && (
                            <div className="flex items-center gap-1">
                              <Edit className="h-3 w-3" />
                              Updated: {new Date(session.updated_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>

                        {session.tags && session.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {session.tags.map((tag, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        {session.audio_url && (
                          <Button size="sm" variant="outline" asChild>
                            <a href={session.audio_url} target="_blank" rel="noopener noreferrer">
                              <Play className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteSession(session.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Sessions</p>
                <p className="text-2xl font-bold">{sessions.length}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">With Audio</p>
                <p className="text-2xl font-bold">
                  {sessions.filter(s => s.audio_url).length}
                </p>
              </div>
              <Play className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Categories</p>
                <p className="text-2xl font-bold">{categories.length}</p>
              </div>
              <Download className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContentManagement;
